import { UserToken } from "@app/store/useUserVarStore";
import fetchTMApi from "@helper/fetchTMApi";
import { shouldShowReactions } from "@helper/shouldShowReactions";
import { getWindowHref } from "@helper/utils";
import {
  EmptyReactionsResponse,
  HandleReactionClick,
  ReactionsData,
  ReactionsDataRequest,
  ReactionsResponse,
  ReactionsSuccessData,
} from "@typings/Reactions";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTMCustomEventAnalyticsCall } from "./useAnalyticsCall/useTMCustomEventAnalyticsCall";
import { useFetchRequest } from "./useFetchRequest";

type useArticleReactionsProps = {
  articleId: string;
  userToken: UserToken;
  publishDate: string;
  uniqueSectionName?: string;
};

export default function useArticleReactions({
  articleId,
  userToken,
  publishDate,
  uniqueSectionName,
}: useArticleReactionsProps) {
  const [reactions, setReactions] = useState<ReactionsData[]>([]);
  const [reactionSetId, setReactionSetId] = useState<string>("");
  const [userReactions, setUserReactions] = useState<string>("0");

  const isValidArticleId = typeof articleId === "string";
  const showReactions = shouldShowReactions(publishDate, uniqueSectionName);
  const TmCustomEvent = useTMCustomEventAnalyticsCall();

  const queryParams = useMemo(
    () => new URLSearchParams({ articleId }).toString(),
    [articleId],
  );

  const memoHeaders = useMemo(
    () => ({
      "user-type": userToken.type || "",
      "user-id": userToken.id || "",
    }),
    [userToken.type, userToken.id],
  );

  // Fetch article reactions with user_reacted flag.
  const { data: reactionsResults, error: reactionsError } = useFetchRequest<
    ReactionsResponse<ReactionsSuccessData> | EmptyReactionsResponse
  >({
    endpoint: `reactions?${queryParams}`,
    headers: memoHeaders,
    skip: !showReactions || !isValidArticleId,
  });

  useEffect(() => {
    if (
      !reactionsError &&
      reactionsResults?.data &&
      "reactions" in reactionsResults.data
    ) {
      setReactions(reactionsResults.data.reactions);
      setReactionSetId(reactionsResults.data.setId || "");
      const userReactedId =
        reactionsResults.data.reactions.find((r) => r.user_reacted)?.id ?? "0";
      setUserReactions(userReactedId);
    }
  }, [reactionsResults, reactionsError]);

  // Handle reaction click
  const handleReactionClick: HandleReactionClick = useCallback(
    async (reactId, variant) => {
      const prevReactId = userReactions;

      if (prevReactId !== "0" && reactId === prevReactId) {
        reactId = "0";
      }

      setReactions((prevReactions) =>
        prevReactions.map((reaction) => {
          if (reaction.id === reactId) {
            return { ...reaction, count: reaction.count + 1 };
          } else if (reaction.id === prevReactId) {
            return { ...reaction, count: reaction.count - 1 };
          }
          return reaction;
        }),
      );
      setUserReactions(reactId);

      try {
        if (!showReactions) {
          throw new Error("Reaction is disabled");
        }

        const url = `reactions/${reactId !== "0" ? "user-reacts" : "delete-user-reacts"}`;
        const requestData: ReactionsDataRequest = {
          articleId,
          userToken,
          reactId,
          setId: reactionSetId,
        };

        const results = await fetchTMApi<EmptyReactionsResponse>(
          url,
          "POST",
          requestData,
        );

        if (results.message === "Success") {
          //sendGaEvent
          TmCustomEvent({
            event: "customEvent",
            eventCategory: "reaction",
            eventAction: `${variant}::${reactId}`,
            eventLabel: getWindowHref(),
          });
        } else {
          setReactions((prevReactions) =>
            prevReactions.map((reaction) => {
              if (reaction.id === reactId) {
                return { ...reaction, count: reaction.count - 1 };
              } else if (reaction.id === prevReactId) {
                return { ...reaction, count: reaction.count + 1 };
              }
              return reaction;
            }),
          );
        }
      } catch (err) {
        console.error("Error posting reaction:", err);
      }
    },
    [
      articleId,
      userToken,
      reactionSetId,
      userReactions,
      showReactions,
      TmCustomEvent,
    ],
  );

  return {
    reactions,
    reactionSetId,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  };
}
